'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'

export default function HostingDashboardClient() {
  const [servers, setServers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const [formData, setFormData] = useState({
    serverName: '',
    ipAddress: '',
    panelUrl: '',
    sshUser: '',
    sshPassword: '',
    status: 'ONLINE'
  })

  useEffect(() => {
    fetch('/api/user/hosting')
      .then(async (res) => {
        if (!res.ok) {
          setServers([])
          setLoading(false)
          return
        }
        const data = await res.json()
        setServers(Array.isArray(data) ? data : [])
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  const startEditing = (server: any) => {
    setFormData({
      serverName: server.serverName || '',
      ipAddress: server.ipAddress || '',
      panelUrl: server.panelUrl || '',
      sshUser: server.sshUser || '',
      sshPassword: server.sshPassword || '',
      status: server.status || 'ONLINE'
    })
    setEditingId(server.id)
  }

  const handleSave = async (e: React.FormEvent, id: string) => {
    e.preventDefault()
    try {
      const res = await fetch('/api/user/hosting', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...formData })
      })
      if (!res.ok) throw new Error('Failed to update server')
      
      const { updatedServer } = await res.json()
      setServers(servers.map(s => s.id === id ? updatedServer : s))
      setEditingId(null)
    } catch (err: any) {
      alert(err.message)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to remove this server from your dashboard?')) return
    try {
      const res = await fetch(`/api/user/hosting?id=${id}`, {
        method: 'DELETE'
      })
      if (!res.ok) throw new Error('Failed to delete server')
      
      setServers(servers.filter(s => s.id !== id))
    } catch (err: any) {
      alert(err.message)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="space-y-6">
      
      <div className="bg-slate-950/80 backdrop-blur-xl border border-slate-800/80 rounded-3xl p-8 shadow-2xl relative overflow-hidden flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="absolute top-0 right-0 w-80 h-80 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="relative z-10 space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 font-mono text-[11px] font-bold uppercase tracking-widest">
            <span>⚡</span> dashboard / infrastructure_nodes
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-100 tracking-tight">Hosting &amp; Server Dashboard</h1>
          <p className="text-slate-400 text-xs sm:text-sm max-w-xl leading-relaxed">
            Manage your provisioned developer nodes, view connection credentials, and monitor server health.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="bg-slate-950/80 border border-slate-800/80 rounded-3xl p-12 text-center text-slate-500 font-mono text-xs shadow-xl">
          Loading infrastructure nodes...
        </div>
      ) : servers.length === 0 ? (
        <div className="bg-slate-950/80 border border-slate-800/80 rounded-3xl p-12 text-center flex flex-col items-center justify-center space-y-4 shadow-xl">
          <div className="w-16 h-16 bg-slate-900 border border-slate-800 rounded-2xl flex items-center justify-center text-2xl mb-2">
            🖥️
          </div>
          <h3 className="text-lg font-bold text-slate-100">No Servers Provisioned</h3>
          <p className="text-slate-400 text-xs max-w-md leading-relaxed">
            You don&apos;t have any active DevYield nodes. Purchase a hosting package from the Points Store to automatically provision your environment.
          </p>
          <Link href="/store" className="mt-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-5 py-2.5 rounded-xl text-xs transition-all shadow-lg shadow-indigo-600/20">
            Go to Points Store
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {servers.map((server) => (
            <div key={server.id} className="bg-slate-950/80 backdrop-blur-xl border border-slate-800/80 p-6 sm:p-8 rounded-3xl shadow-xl">
              {editingId === server.id ? (
                <form onSubmit={(e) => handleSave(e, server.id)} className="space-y-6">
                  <div className="flex justify-between items-center border-b border-slate-800/60 pb-4">
                    <h3 className="text-xs font-mono font-bold text-slate-300 uppercase tracking-widest">Configure Server Credentials</h3>
                    <button 
                      type="button" 
                      onClick={() => handleDelete(server.id)} 
                      className="text-rose-400 hover:text-white font-bold px-3 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 rounded-xl text-xs transition-colors border border-rose-500/20"
                    >
                      Delete Server Record
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest">Server Name</label>
                      <input
                        type="text"
                        value={formData.serverName}
                        onChange={e => setFormData({ ...formData, serverName: e.target.value })}
                        className="dev-input"
                        required
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest">IP Address / Hostname</label>
                      <input
                        type="text"
                        value={formData.ipAddress}
                        onChange={e => setFormData({ ...formData, ipAddress: e.target.value })}
                        className="dev-input font-mono"
                        required
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest">Control Panel URL</label>
                      <input
                        type="text"
                        value={formData.panelUrl}
                        onChange={e => setFormData({ ...formData, panelUrl: e.target.value })}
                        className="dev-input font-mono"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest">SSH Username</label>
                      <input
                        type="text"
                        value={formData.sshUser}
                        onChange={e => setFormData({ ...formData, sshUser: e.target.value })}
                        className="dev-input font-mono"
                      />
                    </div>

                    <div className="space-y-1.5 md:col-span-2">
                      <label className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest">SSH Password / Key Reference</label>
                      <input
                        type="text"
                        value={formData.sshPassword}
                        onChange={e => setFormData({ ...formData, sshPassword: e.target.value })}
                        className="dev-input font-mono"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-800/60">
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="bg-slate-900 hover:bg-slate-800 text-slate-300 font-bold px-4 py-2 rounded-xl text-xs transition-colors border border-slate-800"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-5 py-2 rounded-xl text-xs transition-colors shadow-lg shadow-indigo-600/20"
                    >
                      Save Configuration
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-800/60 pb-4">
                    <div className="space-y-1">
                      <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest">Active Instance</span>
                      <h2 className="text-lg font-bold text-slate-100">{server.serverName}</h2>
                      <p className="text-xs font-mono text-cyan-400">Target: {server.ipAddress}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-3 py-1 rounded-md text-[10px] font-mono font-bold uppercase tracking-wider flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                        {server.status}
                      </span>
                      <button
                        type="button"
                        onClick={() => startEditing(server)}
                        className="bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20 text-cyan-400 font-bold px-3.5 py-1.5 rounded-xl text-xs transition-colors"
                      >
                        ⚙️ Edit
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-slate-900/60 p-4 rounded-2xl border border-slate-800/80 space-y-1 shadow-inner">
                      <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest">Control Panel URL</span>
                      <div className="flex items-center justify-between gap-4">
                        <a href={server.panelUrl} target="_blank" rel="noreferrer" className="text-xs text-indigo-400 font-mono truncate hover:underline">
                          {server.panelUrl}
                        </a>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(server.panelUrl)}
                          className="text-slate-500 hover:text-indigo-400 transition-colors p-1 rounded-lg hover:bg-indigo-500/10 active:scale-95 shrink-0"
                          title="Copy URL"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className="bg-slate-900/60 p-4 rounded-2xl border border-slate-800/80 space-y-1 shadow-inner">
                      <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest">SSH Username</span>
                      <p className="text-xs text-slate-200 font-mono">{server.sshUser}</p>
                    </div>

                    <div className="bg-slate-900/60 p-4 rounded-2xl border border-slate-800/80 space-y-1 md:col-span-2 shadow-inner">
                      <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest">SSH / Root Password</span>
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-xs text-emerald-400 font-mono tracking-wider">{server.sshPassword}</span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(server.sshPassword)}
                          className="bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1 rounded-xl text-xs font-mono font-bold transition-colors flex items-center gap-1.5 border border-slate-700"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          {copied ? 'Copied!' : 'Copy Password'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}